import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import AdminNav from "../../components/AdminNav";
import LoadingIndicator from "../../components/LoadingIndicator";
import { getAdminUsers, blockUser, adminApproveB2B, adminRevokeB2B, adminApproveCorporate, adminRevokeCorporate, adminSetBillingMode, adminListInvoices, adminMarkInvoicePaid, adminApproveAgent, adminRevokeAgent, adminApproveFleetOwner, adminRevokeFleetOwner, adminResetUserPassword } from "../../api/client";
import ResetPasswordModal from "../../components/ResetPasswordModal";

// B2B/Partner and Corporate are independent tracks (like Agent/Fleet Owner)
// — a company can hold either, both, or neither, each admin-approved on its
// own timeline without affecting the other.
const B2B_STATUS_BADGE = {
  none:     { cls: "",        label: "No B2B" },
  pending:  { cls: "warning", label: "⏳ B2B Pending" },
  approved: { cls: "info",    label: "🔌 B2B/Partner" },
  revoked:  { cls: "danger",  label: "❌ B2B Revoked" },
};

const CORPORATE_STATUS_BADGE = {
  none:     { cls: "",        label: "No Corporate" },
  pending:  { cls: "warning", label: "⏳ Corporate Pending" },
  approved: { cls: "info",    label: "🏛️ Corporate" },
  revoked:  { cls: "danger",  label: "❌ Corporate Revoked" },
};

const AGENT_STATUS_BADGE = {
  pending:  { cls: "warning", label: "⏳ Agent Pending" },
  approved: { cls: "info",    label: "🧑‍💼 Agent" },
  revoked:  { cls: "danger",  label: "❌ Agent Revoked" },
};

const FLEET_STATUS_BADGE = {
  pending:  { cls: "warning", label: "⏳ Fleet Pending" },
  approved: { cls: "info",    label: "🚚 Fleet Owner" },
  revoked:  { cls: "danger",  label: "❌ Fleet Revoked" },
};

const INVOICE_STATUS_BADGE = {
  unpaid:   { cls: "warning", label: "UNPAID" },
  paid:     { cls: "info",    label: "PAID" },
  overdue:  { cls: "danger",  label: "OVERDUE" },
  combined: { cls: "",        label: "COMBINED" },
};

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState("all"); // "all" | "pending" | "approved"
  const [search, setSearch] = useState("");
  const [blockReasonMap, setBlockReasonMap] = useState({});

  // Business approve modal state — shared between the B2B and Corporate
  // tracks (same company/billing fields either way); approveTrack picks
  // which one gets approved on submit.
  const [approveModal, setApproveModal] = useState(null); // { userId, name }
  const [approveTrack, setApproveTrack] = useState("b2b"); // "b2b" | "corporate"
  const [approveForm, setApproveForm] = useState({ companyName: "", cacNumber: "", companyAddress: "", officialEmail: "", phoneNumber: "", website: "", billingTerms: "per_order", billingMode: "pay_now" });
  const [generatedKey, setGeneratedKey] = useState(null);

  // Invoices modal state
  const [invoicesModal, setInvoicesModal] = useState(null); // { userId, name }
  const [resetPasswordUser, setResetPasswordUser] = useState(null); // full user object
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceNoteMap, setInvoiceNoteMap] = useState({});

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (tab === "pending") params.b2bStatus = "pending";
      if (tab === "approved") params.b2bStatus = "approved";
      if (search) params.search = search;
      const { data } = await getAdminUsers(params);
      setUsers(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab]);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const handleBlock = async (user, targetState) => {
    setBusyId(user._id);
    setError("");
    try {
      const reason = blockReasonMap[user._id] || (targetState ? "Platform policy violation" : "");
      await blockUser(user._id, targetState, reason);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not update block status.");
    } finally {
      setBusyId(null);
    }
  };

  const handleApproveBusiness = async () => {
    if (!approveModal) return;
    setBusyId(approveModal.userId);
    setError("");
    try {
      if (approveTrack === "corporate") {
        await adminApproveCorporate(approveModal.userId, approveForm);
        // Corporate approval issues no API key, so there's no "here's your
        // key" screen to show (unlike B2B below) — close the modal and let
        // the reloaded card's badge/buttons confirm it, same as Agent/Fleet
        // Owner approval already does.
        setApproveModal(null);
      } else {
        const { data } = await adminApproveB2B(approveModal.userId, approveForm);
        setGeneratedKey(data.apiKey);
      }
      await load();
    } catch (err) {
      setError(err.response?.data?.error || `Could not approve ${approveTrack === "corporate" ? "Corporate" : "B2B"} access.`);
    } finally {
      setBusyId(null);
    }
  };

  const handleRevokeB2B = async (userId) => {
    if (!window.confirm("Revoke B2B access for this user? Their API key will be invalidated.")) return;
    setBusyId(userId);
    setError("");
    try {
      await adminRevokeB2B(userId);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not revoke B2B access.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRevokeCorporate = async (userId) => {
    if (!window.confirm("Revoke Corporate access for this user?")) return;
    setBusyId(userId);
    setError("");
    try {
      await adminRevokeCorporate(userId);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not revoke Corporate access.");
    } finally {
      setBusyId(null);
    }
  };

  const handleSetBillingMode = async (user, mode) => {
    setBusyId(user._id);
    setError("");
    try {
      await adminSetBillingMode(user._id, mode);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not update billing mode.");
    } finally {
      setBusyId(null);
    }
  };

  const openInvoices = async (user) => {
    setInvoicesModal({ userId: user._id, name: user.name });
    setInvoicesLoading(true);
    try {
      const { data } = await adminListInvoices({ businessUser: user._id });
      setInvoices(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load invoices.");
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    setBusyId(invoiceId);
    setError("");
    try {
      await adminMarkInvoicePaid(invoiceId, invoiceNoteMap[invoiceId] || "");
      const { data } = await adminListInvoices({ businessUser: invoicesModal.userId });
      setInvoices(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not mark invoice paid.");
    } finally {
      setBusyId(null);
    }
  };

  const handleApproveAgent = async (userId) => {
    setBusyId(userId);
    setError("");
    try {
      await adminApproveAgent(userId);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not approve Agent access.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRevokeAgent = async (userId) => {
    if (!window.confirm("Revoke Agent access for this user?")) return;
    setBusyId(userId);
    setError("");
    try {
      await adminRevokeAgent(userId);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not revoke Agent access.");
    } finally {
      setBusyId(null);
    }
  };

  const handleApproveFleetOwner = async (userId) => {
    setBusyId(userId);
    setError("");
    try {
      await adminApproveFleetOwner(userId);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not approve Fleet Owner access.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRevokeFleetOwner = async (userId) => {
    if (!window.confirm("Revoke Fleet Owner access for this user? All linked dispatchers will be unlinked.")) return;
    setBusyId(userId);
    setError("");
    try {
      await adminRevokeFleetOwner(userId);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not revoke Fleet Owner access.");
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = users.filter(u => u.b2bApprovalStatus === "pending" || u.corporateApprovalStatus === "pending").length;

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">

        <div className="brand-header">
          <span className="brand-logo">👥</span>
          <span className="brand-name">User Management</span>
          <p className="brand-tagline">Manage user accounts, B2B access, and platform blocks</p>
        </div>

        {/* Filter Tabs */}
        <div style={{ display: "flex", gap: "10px", marginBottom: "16px", flexWrap: "wrap" }}>
          {[
            { key: "all",     label: `All Users` },
            { key: "pending", label: `⏳ Business Requests${pendingCount > 0 ? ` (${pendingCount})` : ""}` },
            { key: "approved",label: "✅ Business Accounts" },
          ].map(t => (
            <button key={t.key}
              className={tab === t.key ? "" : "secondary"}
              style={{ width: "auto", padding: "8px 16px", marginTop: 0 }}
              onClick={() => setTab(t.key)}
            >{t.label}</button>
          ))}
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
          <input
            type="text"
            placeholder="Search by name, email or phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, margin: 0 }}
          />
          <button type="submit" style={{ width: "auto", padding: "10px 20px", marginTop: 0 }}>Search</button>
        </form>

        {error && <p className="error">{error}</p>}

        {loading && <LoadingIndicator label="Loading users..." />}

        {!loading && users.length === 0 && (
          <div className="empty-state">
            <span className="empty-state-icon">👤</span>
            <p className="empty-state-text">No users found.</p>
          </div>
        )}

        {/* User Cards */}
        {!loading && users.map(user => {
          const b2b = B2B_STATUS_BADGE[user.b2bApprovalStatus] || B2B_STATUS_BADGE.none;
          const corporate = CORPORATE_STATUS_BADGE[user.corporateApprovalStatus] || CORPORATE_STATUS_BADGE.none;
          return (
            <div key={user._id} className="delivery-card" style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "8px" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "16px" }}>{user.name}</div>
                  <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>{user.email} · {user.phone}</div>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                    Joined {formatDate(user.createdAt)} · Tier: <strong>{user.membershipTier || "standard"}</strong>
                  </div>
                  {user.companyName && (
                    <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginTop: "2px" }}>
                      🏢 {user.companyName}{user.cacNumber ? ` (CAC ${user.cacNumber})` : ""} · {user.billingTerms === "invoiced" ? "Invoice Billing" : "Per Order"}
                      {user.billingTerms === "invoiced" && (
                        <> · {user.billingMode === "consolidated" ? "Consolidated" : "Pay Now"}</>
                      )}
                      {(user.phoneNumber || user.officialPhone) && (
                        <> · 📞 {user.phoneNumber || user.officialPhone}</>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                  {user.b2bApprovalStatus && user.b2bApprovalStatus !== "none" && (
                    <span className={`status-badge ${b2b.cls}`}>{b2b.label}</span>
                  )}
                  {user.corporateApprovalStatus && user.corporateApprovalStatus !== "none" && (
                    <span className={`status-badge ${corporate.cls}`}>{corporate.label}</span>
                  )}
                  {user.agentApprovalStatus && user.agentApprovalStatus !== "none" && (
                    <span className={`status-badge ${AGENT_STATUS_BADGE[user.agentApprovalStatus].cls}`}>
                      {AGENT_STATUS_BADGE[user.agentApprovalStatus].label}
                    </span>
                  )}
                  {user.fleetOwnerApprovalStatus && user.fleetOwnerApprovalStatus !== "none" && (
                    <span className={`status-badge ${FLEET_STATUS_BADGE[user.fleetOwnerApprovalStatus].cls}`}>
                      {FLEET_STATUS_BADGE[user.fleetOwnerApprovalStatus].label}
                    </span>
                  )}
                  {user.isBlocked && <span className="status-badge danger">🚫 Blocked</span>}
                </div>
              </div>

              {/* Actions Row */}
              <div style={{ display: "flex", gap: "8px", marginTop: "14px", flexWrap: "wrap" }}>
                {/* Block / Unblock */}
                {user.isBlocked ? (
                  <button
                    className="secondary"
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                    disabled={busyId === user._id}
                    onClick={() => handleBlock(user, false)}
                  >✅ Unblock User</button>
                ) : (
                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <input
                      type="text"
                      placeholder="Block reason..."
                      value={blockReasonMap[user._id] || ""}
                      onChange={e => setBlockReasonMap(prev => ({ ...prev, [user._id]: e.target.value }))}
                      style={{ width: "180px", margin: 0, padding: "7px 10px", fontSize: "12px" }}
                    />
                    <button
                      className="secondary"
                      style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px", borderColor: "var(--danger)", color: "var(--danger)" }}
                      disabled={busyId === user._id}
                      onClick={() => handleBlock(user, true)}
                    >🚫 Block User</button>
                  </div>
                )}

                <button
                  className="secondary"
                  style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                  onClick={() => setResetPasswordUser(user)}
                >🔑 Reset Password</button>

                {/* B2B/Partner Actions — independent of Corporate below; a
                    company can hold both, either, or neither. */}
                {(user.b2bApprovalStatus === "none" || user.b2bApprovalStatus === "pending") && (
                  <button
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                    disabled={busyId === user._id}
                    onClick={() => { setApproveModal({ userId: user._id, name: user.name }); setApproveTrack("b2b"); setGeneratedKey(null); setApproveForm({ companyName: user.companyName || "", cacNumber: user.cacNumber || "", companyAddress: user.companyAddress || "", officialEmail: user.officialEmail || "", phoneNumber: user.phoneNumber || user.officialPhone || user.phone || "", website: user.website || "", billingTerms: user.billingTerms || "per_order", billingMode: user.billingMode || "pay_now" }); }}
                  >🔑 Approve B2B Access</button>
                )}
                {user.b2bApprovalStatus === "approved" && (
                  <button
                    className="secondary"
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px", borderColor: "var(--warning)", color: "var(--warning)" }}
                    disabled={busyId === user._id}
                    onClick={() => handleRevokeB2B(user._id)}
                  >🔒 Revoke B2B Access</button>
                )}
                {user.b2bApprovalStatus === "revoked" && (
                  <button
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                    disabled={busyId === user._id}
                    onClick={() => { setApproveModal({ userId: user._id, name: user.name }); setApproveTrack("b2b"); setGeneratedKey(null); setApproveForm({ companyName: user.companyName || "", cacNumber: user.cacNumber || "", companyAddress: user.companyAddress || "", officialEmail: user.officialEmail || "", phoneNumber: user.phoneNumber || user.officialPhone || user.phone || "", website: user.website || "", billingTerms: user.billingTerms || "per_order", billingMode: user.billingMode || "pay_now" }); }}
                  >🔄 Re-approve B2B</button>
                )}

                {/* Corporate Actions — independent of B2B/Partner above. */}
                {(user.corporateApprovalStatus === "none" || user.corporateApprovalStatus === "pending") && (
                  <button
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                    disabled={busyId === user._id}
                    onClick={() => { setApproveModal({ userId: user._id, name: user.name }); setApproveTrack("corporate"); setGeneratedKey(null); setApproveForm({ companyName: user.companyName || "", cacNumber: user.cacNumber || "", companyAddress: user.companyAddress || "", officialEmail: user.officialEmail || "", phoneNumber: user.phoneNumber || user.officialPhone || user.phone || "", website: user.website || "", billingTerms: user.billingTerms || "invoiced", billingMode: user.billingMode || "pay_now" }); }}
                  >🏛️ Approve Corporate Access</button>
                )}
                {user.corporateApprovalStatus === "approved" && (
                  <button
                    className="secondary"
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px", borderColor: "var(--warning)", color: "var(--warning)" }}
                    disabled={busyId === user._id}
                    onClick={() => handleRevokeCorporate(user._id)}
                  >🔒 Revoke Corporate Access</button>
                )}
                {user.corporateApprovalStatus === "revoked" && (
                  <button
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                    disabled={busyId === user._id}
                    onClick={() => { setApproveModal({ userId: user._id, name: user.name }); setApproveTrack("corporate"); setGeneratedKey(null); setApproveForm({ companyName: user.companyName || "", cacNumber: user.cacNumber || "", companyAddress: user.companyAddress || "", officialEmail: user.officialEmail || "", phoneNumber: user.phoneNumber || user.officialPhone || user.phone || "", website: user.website || "", billingTerms: user.billingTerms || "invoiced", billingMode: user.billingMode || "pay_now" }); }}
                  >🔄 Re-approve Corporate</button>
                )}
                {(user.b2bApprovalStatus === "approved" || user.corporateApprovalStatus === "approved") && user.billingTerms === "invoiced" && (
                  <>
                    <button
                      className="secondary"
                      style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                      disabled={busyId === user._id}
                      onClick={() => handleSetBillingMode(user, user.billingMode === "consolidated" ? "pay_now" : "consolidated")}
                    >
                      {user.billingMode === "consolidated" ? "Switch to Pay Now" : "Switch to Consolidated"}
                    </button>
                    <button
                      className="secondary"
                      style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                      onClick={() => openInvoices(user)}
                    >🧾 Invoices</button>
                  </>
                )}

                {/* Agent Actions */}
                {(user.agentApprovalStatus === "pending" || user.agentApprovalStatus === "revoked") && (
                  <button
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                    disabled={busyId === user._id}
                    onClick={() => handleApproveAgent(user._id)}
                  >🧑‍💼 Approve Agent</button>
                )}
                {user.agentApprovalStatus === "approved" && (
                  <button
                    className="secondary"
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px", borderColor: "var(--warning)", color: "var(--warning)" }}
                    disabled={busyId === user._id}
                    onClick={() => handleRevokeAgent(user._id)}
                  >🔒 Revoke Agent Access</button>
                )}

                {/* Fleet Owner Actions */}
                {(user.fleetOwnerApprovalStatus === "pending" || user.fleetOwnerApprovalStatus === "revoked") && (
                  <button
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                    disabled={busyId === user._id}
                    onClick={() => handleApproveFleetOwner(user._id)}
                  >🚚 Approve Fleet Owner</button>
                )}
                {user.fleetOwnerApprovalStatus === "approved" && (
                  <button
                    className="secondary"
                    style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px", borderColor: "var(--warning)", color: "var(--warning)" }}
                    disabled={busyId === user._id}
                    onClick={() => handleRevokeFleetOwner(user._id)}
                  >🔒 Revoke Fleet Owner Access</button>
                )}
              </div>
            </div>
          );
        })}

        {/* B2B Approve Modal — portaled to <body>: .page has backdrop-filter,
            which creates a containing block for position:fixed descendants,
            so a nested fixed modal centers on the scrollable page instead of
            the viewport. */}
        {approveModal && createPortal(
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }}>
            <div className="delivery-card" style={{ maxWidth: "480px", width: "100%", margin: 0, maxHeight: "calc(100vh - 40px)", overflowY: "auto", WebkitOverflowScrolling: "touch", boxSizing: "border-box" }}>
              <h3 style={{ marginBottom: "16px" }}>Approve {approveTrack === "corporate" ? "Corporate" : "B2B"} Access — {approveModal.name}</h3>
              <p className="hint" style={{ marginBottom: "16px" }}>
                {approveTrack === "corporate"
                  ? "Corporate: booking dashboard, consolidated invoices — no API access. Independent of any B2B/Partner status this account has."
                  : "B2B/Partner: API key, bulk orders, webhooks. Independent of any Corporate status this account has."}
              </p>

              {generatedKey ? (
                <>
                  <div className="goboot-ai-banner" style={{ marginBottom: "16px" }}>
                    <div className="ai-banner-icon">✅</div>
                    <div className="ai-banner-text">
                      <strong>B2B Access Approved!</strong> Share the API key below with the user securely. It will NOT be shown again.
                    </div>
                  </div>
                  <div style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", padding: "12px 16px", fontFamily: "monospace", fontSize: "13px", wordBreak: "break-all", marginBottom: "16px", color: "var(--success-hover)" }}>
                    {generatedKey}
                  </div>
                  <button onClick={() => { navigator.clipboard?.writeText(generatedKey); }} style={{ width: "100%", marginBottom: "8px" }}>📋 Copy API Key</button>
                  <button className="secondary" style={{ width: "100%" }} onClick={() => setApproveModal(null)}>Close</button>
                </>
              ) : (
                <>
                  <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                    <label style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>Company Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Acme Logistics Ltd."
                      value={approveForm.companyName}
                      onChange={e => setApproveForm(f => ({ ...f, companyName: e.target.value }))}
                      style={{ width: "100%", margin: 0 }}
                    />
                  </div>
                  <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start", marginTop: "12px" }}>
                    <label style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>CAC Registration Number</label>
                    <input
                      type="text"
                      placeholder="e.g. RC1234567"
                      value={approveForm.cacNumber}
                      onChange={e => setApproveForm(f => ({ ...f, cacNumber: e.target.value }))}
                      style={{ width: "100%", margin: 0 }}
                    />
                  </div>
                  <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start", marginTop: "12px" }}>
                    <label style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>Company Address</label>
                    <input
                      type="text"
                      placeholder="Registered business address"
                      value={approveForm.companyAddress}
                      onChange={e => setApproveForm(f => ({ ...f, companyAddress: e.target.value }))}
                      style={{ width: "100%", margin: 0 }}
                    />
                  </div>
                  <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start", marginTop: "12px" }}>
                    <label style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>Official Email</label>
                    <input
                      type="email"
                      placeholder="e.g. ops@acmelogistics.com"
                      value={approveForm.officialEmail}
                      onChange={e => setApproveForm(f => ({ ...f, officialEmail: e.target.value }))}
                      style={{ width: "100%", margin: 0 }}
                    />
                  </div>
                  <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start", marginTop: "12px" }}>
                    <label style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>Phone Number</label>
                    <input
                      type="tel"
                      placeholder="e.g. 08012345678"
                      value={approveForm.phoneNumber}
                      onChange={e => setApproveForm(f => ({ ...f, phoneNumber: e.target.value }))}
                      style={{ width: "100%", margin: 0 }}
                    />
                  </div>
                  <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start", marginTop: "12px" }}>
                    <label style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>Website</label>
                    <input
                      type="url"
                      placeholder="https://acmelogistics.com"
                      value={approveForm.website}
                      onChange={e => setApproveForm(f => ({ ...f, website: e.target.value }))}
                      style={{ width: "100%", margin: 0 }}
                    />
                  </div>
                  <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start", marginTop: "12px" }}>
                    <label style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>Billing Terms</label>
                    <select
                      value={approveForm.billingTerms}
                      onChange={e => setApproveForm(f => ({ ...f, billingTerms: e.target.value }))}
                      style={{ width: "100%", margin: 0, background: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)", borderRadius: "8px", padding: "10px" }}
                    >
                      <option value="per_order">Per Order (Pay as you go)</option>
                      <option value="invoiced">Invoiced (Monthly billing)</option>
                    </select>
                  </div>
                  {approveForm.billingTerms === "invoiced" && (
                    <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start", marginTop: "12px" }}>
                      <label style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>Billing Mode</label>
                      <select
                        value={approveForm.billingMode}
                        onChange={e => setApproveForm(f => ({ ...f, billingMode: e.target.value }))}
                        style={{ width: "100%", margin: 0, background: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)", borderRadius: "8px", padding: "10px" }}
                      >
                        <option value="pay_now">Pay Now (each invoice paid on its own)</option>
                        <option value="consolidated">Consolidated (unpaid invoices auto-combine)</option>
                      </select>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                    <button onClick={handleApproveBusiness} disabled={busyId === approveModal.userId} style={{ flex: 1 }}>
                      {busyId === approveModal.userId ? "Approving..." : approveTrack === "corporate" ? "✅ Approve Corporate Access" : "✅ Approve & Generate API Key"}
                    </button>
                    <button className="secondary" onClick={() => setApproveModal(null)} style={{ flex: 1 }}>Cancel</button>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body
        )}

        {/* Invoices Modal — also portaled, same containing-block reason. */}
        {invoicesModal && createPortal(
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }}>
            <div className="delivery-card" style={{ maxWidth: "560px", width: "100%", margin: 0, maxHeight: "calc(100vh - 40px)", overflowY: "auto", WebkitOverflowScrolling: "touch", boxSizing: "border-box" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h3 style={{ margin: 0 }}>Invoices — {invoicesModal.name}</h3>
                <button className="secondary" style={{ width: "auto", padding: "4px 12px", marginTop: 0 }} onClick={() => setInvoicesModal(null)}>✕ Close</button>
              </div>

              {invoicesLoading ? (
                <LoadingIndicator label="Loading invoices..." compact />
              ) : invoices.length === 0 ? (
                <p className="hint">No invoices for this business yet.</p>
              ) : (
                <>
                  <div className="quote-box" style={{ marginBottom: "12px", background: "rgba(255,255,255,0.03)" }}>
                    <span className="label" style={{ fontSize: "11px", color: "var(--text-muted)", display: "block" }}>Total Owed</span>
                    <strong style={{ fontSize: "20px", color: "var(--warning-hover)" }}>
                      ₦{invoices.filter(inv => inv.status === "unpaid").reduce((sum, inv) => sum + inv.totalAmount, 0).toLocaleString()}
                    </strong>
                  </div>
                  {invoices.map(inv => {
                  const badge = INVOICE_STATUS_BADGE[inv.status] || INVOICE_STATUS_BADGE.unpaid;
                  return (
                    <div key={inv._id} className="history-row" style={{ marginBottom: "10px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <strong>Invoice #{inv._id.slice(-6).toUpperCase()}</strong>
                          <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                            {inv.orders.length} deliveries · Due: {new Date(inv.dueDate).toLocaleDateString()}
                          </p>
                          {inv.status === "combined" && inv.combinedInto && (
                            <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                              Combined into Invoice #{inv.combinedInto.toString().slice(-6).toUpperCase()}
                            </p>
                          )}
                          {inv.status === "paid" && (
                            <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                              Paid {new Date(inv.paidAt).toLocaleDateString()} via {inv.paymentMethod}{inv.paidBy === "admin" ? " (admin)" : ""}
                            </p>
                          )}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <strong style={{ fontSize: "16px" }}>₦{inv.totalAmount.toLocaleString()}</strong>
                          <span className={`status-badge ${badge.cls}`} style={{ marginLeft: "8px" }}>{badge.label}</span>
                        </div>
                      </div>

                      {inv.status === "unpaid" && (
                        <div style={{ display: "flex", gap: "8px", marginTop: "10px", flexWrap: "wrap", alignItems: "center" }}>
                          <input
                            type="text"
                            placeholder="Note (e.g. bank transfer ref)"
                            value={invoiceNoteMap[inv._id] || ""}
                            onChange={e => setInvoiceNoteMap(m => ({ ...m, [inv._id]: e.target.value }))}
                            style={{ width: "220px", margin: 0, padding: "6px 10px", fontSize: "12px" }}
                          />
                          <button
                            className="secondary"
                            style={{ width: "auto", padding: "6px 14px", fontSize: "12px", marginTop: 0 }}
                            disabled={busyId === inv._id}
                            onClick={() => handleMarkPaid(inv._id)}
                          >
                            {busyId === inv._id ? "..." : "✅ Mark Paid (Offline Transfer)"}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                </>
              )}
            </div>
          </div>,
          document.body
        )}

        {resetPasswordUser && (
          <ResetPasswordModal
            name={resetPasswordUser.name}
            onSubmit={(newPassword) => adminResetUserPassword(resetPasswordUser._id, newPassword)}
            onClose={() => setResetPasswordUser(null)}
          />
        )}
      </div>
    </div>
  );
}
